import { FileTextOutlined, PayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { InvoiceRow, PaymentRow, SettleRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { hasPermission } from '@hq/permission';
import {
  PAY_METHOD_OPTIONS as PAY_METHODS,
  settleStatusMeta,
  formatAmount,
  formatDisplayTime,
  payMethodLabel
} from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { hq } from '../api/client';
import { sumWidth } from '@hq/ui-kit';
import { useAuthStore } from '../stores/auth';

const PAGE_SIZE = 20;

const statusTag = (value: string): ReactElement => {
  const meta = settleStatusMeta(value);
  return <Tag color={meta.color}>{meta.text}</Tag>;
};

type StatusFilter = 'ALL' | 'CONFIRMED' | 'INVOICED' | 'PAID';

interface InvoiceDraft {
  invoiceNo: string;
  invoiceCode: string;
  amount: number;
  taxAmount: number;
  invoiceAt: string;
  remark: string;
}

interface PaymentDraft {
  payAmount: number;
  payMethod: string;
  payAt: string;
  voucherNo: string;
  remark: string;
}

/** 发票与付款：按对账单登记发票（价税合计）与付款流水，与运营端结算中心同源。 */
export default function SettleInvoice() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);
  const canManage = profile?.level === 'admin' || hasPermission(profile?.perms, 'wl:settle:manage');

  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [current, setCurrent] = useState<SettleRow | null>(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [invoiceForm] = Form.useForm<InvoiceDraft>();
  const [paymentForm] = Form.useForm<PaymentDraft>();

  const list = useQuery({
    queryKey: ['settle', 'invoice-picker', status],
    placeholderData: keepPreviousData,
    queryFn: () =>
      hq.billing.settle.list({
        size: PAGE_SIZE,
        settleStatus: status === 'ALL' ? undefined : status
      })
  });

  const invoices = useQuery({
    queryKey: ['settle', 'invoices', current?.id],
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.settle.invoices(current?.id),
    enabled: Boolean(current?.id)
  });

  const payments = useQuery({
    queryKey: ['settle', 'payments', current?.id],
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.settle.payments(current?.id),
    enabled: Boolean(current?.id)
  });

  const refreshCurrent = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['settle'] }),
      invoices.refetch(),
      payments.refetch(),
      list.refetch()
    ]);
  };

  const registerInvoice = useMutation({
    mutationFn: (draft: InvoiceDraft) =>
      hq.billing.settle.invoice(current?.id ?? '', {
        invoiceNo: draft.invoiceNo,
        invoiceCode: draft.invoiceCode || undefined,
        amount: draft.amount,
        taxAmount: draft.taxAmount || undefined,
        invoiceAt: draft.invoiceAt ? dayjs(draft.invoiceAt).format('YYYY-MM-DD HH:mm:ss') : undefined,
        remark: draft.remark || undefined
      }),
    onSuccess: async () => {
      message.success('发票已登记');
      setInvoiceOpen(false);
      invoiceForm.resetFields();
      await refreshCurrent();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '登记发票失败')
  });

  const registerPayment = useMutation({
    mutationFn: (draft: PaymentDraft) =>
      hq.billing.settle.payment(current?.id ?? '', {
        payAmount: draft.payAmount,
        payMethod: draft.payMethod || undefined,
        payAt: draft.payAt ? dayjs(draft.payAt).format('YYYY-MM-DD HH:mm:ss') : undefined,
        voucherNo: draft.voucherNo || undefined,
        remark: draft.remark || undefined
      }),
    onSuccess: async () => {
      message.success('付款已登记');
      setPaymentOpen(false);
      paymentForm.resetFields();
      await refreshCurrent();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '登记付款失败')
  });

  const unpaid = current ? current.payableAmount - current.paidAmount : 0;

  const columns: ColumnsType<SettleRow> = [
    {
      title: '对账单号',
      dataIndex: 'bizNo',
      width: 190,
      render: (value: string, row) => (
        <Typography.Link strong={row.id === current?.id} onClick={() => setCurrent(row)}>
          {value}
        </Typography.Link>
      )
    },
    { title: '期间', dataIndex: 'settlePeriod', width: 100, render: (value: string | null) => value || '-' },
    { title: '收款方', dataIndex: 'payeeName', width: 160, ellipsis: true },
    {
      title: '应付',
      dataIndex: 'payableAmount',
      width: 130,
      align: 'right',
      render: (value: number) => formatAmount(value)
    },
    {
      title: '已付',
      dataIndex: 'paidAmount',
      width: 130,
      align: 'right',
      render: (value: number) => formatAmount(value)
    },
    { title: '状态', dataIndex: 'settleStatus', width: 100, render: (value: string) => statusTag(value) }
  ];

  const invoiceColumns: ColumnsType<InvoiceRow> = [
    { title: '发票号', dataIndex: 'invoiceNo', width: 160 },
    { title: '代码', dataIndex: 'invoiceCode', width: 130, render: (value: string | null) => value || '-' },
    { title: '含税金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '税额', dataIndex: 'taxAmount', width: 110, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '开票时间', dataIndex: 'invoiceAt', width: 150, render: (value: string | null) => formatDisplayTime(value) },
    { title: '状态', dataIndex: 'status', width: 90, render: (value: string | null) => value || '-' }
  ];

  const paymentColumns: ColumnsType<PaymentRow> = [
    { title: '付款单号', dataIndex: 'payNo', width: 180, render: (value: string | null) => value || '-' },
    { title: '金额', dataIndex: 'payAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '方式', dataIndex: 'payMethod', width: 110, render: (value: string | null) => payMethodLabel(value) },
    { title: '付款时间', dataIndex: 'payAt', width: 150, render: (value: string | null) => formatDisplayTime(value) },
    { title: '凭证号', dataIndex: 'voucherNo', width: 140, render: (value: string | null) => value || '-' }
  ];

  return (
    <Row gutter={16}>
      <Col xs={24} xl={11}>
        <Card
          className="page-card"
          title="对账单"
          extra={<ReloadOutlined spin={list.isFetching} style={{ cursor: 'pointer' }} onClick={() => list.refetch()} />}
        >
          <Segmented<StatusFilter>
            value={status}
            options={[
              { label: '全部', value: 'ALL' },
              { label: '已确认', value: 'CONFIRMED' },
              { label: '已开票', value: 'INVOICED' },
              { label: '已付清', value: 'PAID' }
            ]}
            onChange={(value) => setStatus(value)}
            style={{ marginBottom: 12 }}
          />
          {list.isError ? (
            <Alert
              type="error"
              showIcon
              message="对账单加载失败"
              description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
            />
          ) : null}
          <Table<SettleRow>
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={list.data?.items ?? []}
            loading={list.isPending}
            pagination={false}
            scroll={{ x: sumWidth(columns), y: 540 }}
            locale={{ emptyText: '暂无对账单' }}
            onRow={(row) => ({ onClick: () => setCurrent(row), style: { cursor: 'pointer' } })}
          />
        </Card>
      </Col>

      <Col xs={24} xl={13}>
        <Card
          className="page-card"
          title={
            <Space>
              发票与付款
              {current ? <Tag color="blue">{current.bizNo}</Tag> : null}
            </Space>
          }
          extra={current ? statusTag(current.settleStatus) : null}
        >
          {!current ? (
            <Empty description="请在左侧选择一张对账单" />
          ) : (
            <>
              <Descriptions size="small" column={3} bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label="应付合计">{formatAmount(current.payableAmount)}</Descriptions.Item>
                <Descriptions.Item label="已付">{formatAmount(current.paidAmount)}</Descriptions.Item>
                <Descriptions.Item label="未付">
                  <Typography.Text type={unpaid > 0 ? 'danger' : undefined}>{formatAmount(unpaid)}</Typography.Text>
                </Descriptions.Item>
              </Descriptions>

              <Space style={{ marginBottom: 12 }}>
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  disabled={!canManage}
                  onClick={() => {
                    invoiceForm.setFieldsValue({
                      invoiceNo: '',
                      invoiceCode: '',
                      amount: Number(unpaid.toFixed(2)),
                      taxAmount: 0,
                      invoiceAt: '',
                      remark: ''
                    });
                    setInvoiceOpen(true);
                  }}
                >
                  登记发票
                </Button>
                <Button
                  icon={<PayCircleOutlined />}
                  disabled={!canManage}
                  onClick={() => {
                    paymentForm.setFieldsValue({
                      payAmount: Number(unpaid.toFixed(2)),
                      payMethod: 'TRANSFER',
                      payAt: '',
                      voucherNo: '',
                      remark: ''
                    });
                    setPaymentOpen(true);
                  }}
                >
                  登记付款
                </Button>
                {!canManage ? <Typography.Text type="secondary">需 wl:settle:manage 权限</Typography.Text> : null}
              </Space>

              <Tabs
                items={[
                  {
                    key: 'invoice',
                    label: `发票（${invoices.data?.length ?? 0}）`,
                    children: (
                      <Table<InvoiceRow>
                        rowKey="id"
                        size="small"
                        columns={invoiceColumns}
                        dataSource={invoices.data ?? []}
                        loading={Boolean(current?.id) && invoices.isPending}
                        pagination={false}
                        scroll={{ x: sumWidth(invoiceColumns) }}
                        locale={{ emptyText: '尚未登记发票' }}
                      />
                    )
                  },
                  {
                    key: 'payment',
                    label: `付款（${payments.data?.length ?? 0}）`,
                    children: (
                      <Table<PaymentRow>
                        rowKey="id"
                        size="small"
                        columns={paymentColumns}
                        dataSource={payments.data ?? []}
                        loading={Boolean(current?.id) && payments.isPending}
                        pagination={false}
                        scroll={{ x: sumWidth(paymentColumns) }}
                        locale={{ emptyText: '尚未登记付款' }}
                      />
                    )
                  }
                ]}
              />
            </>
          )}
        </Card>
      </Col>

      <Modal
        title="登记发票"
        open={invoiceOpen}
        onCancel={() => setInvoiceOpen(false)}
        okText="登记"
        cancelText="取消"
        confirmLoading={registerInvoice.isPending}
        onOk={() => invoiceForm.submit()}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`未付金额 ${formatAmount(unpaid)}`}
          description="录入的是发票**价税合计**；累计开票金额不得超过应付含税，税额不得大于价税合计。"
        />
        <Form form={invoiceForm} layout="vertical" onFinish={(values) => registerInvoice.mutate(values)}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="invoiceNo" label="发票号码" rules={[{ required: true, message: '请填写发票号码' }]}>
                <Input maxLength={32} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="invoiceCode" label="发票代码">
                <Input maxLength={32} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="amount" label="价税合计" rules={[{ required: true, message: '请填写金额' }]}>
                <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="taxAmount" label="其中税额">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="invoiceAt" label="开票日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="remark" label="备注">
                <Input maxLength={128} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="登记付款"
        open={paymentOpen}
        onCancel={() => setPaymentOpen(false)}
        okText="登记"
        cancelText="取消"
        confirmLoading={registerPayment.isPending}
        onOk={() => paymentForm.submit()}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`未付金额 ${formatAmount(unpaid)}`}
          description="付款登记后累加「已付金额」，付清置为已付清；请与银行回单保持一致。"
        />
        <Form form={paymentForm} layout="vertical" onFinish={(values) => registerPayment.mutate(values)}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="payAmount" label="付款金额" rules={[{ required: true, message: '请填写金额' }]}>
                <InputNumber min={0.01} precision={2} style={{ width: '100%' }} max={unpaid} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payMethod" label="付款方式">
                <Select options={PAY_METHODS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payAt" label="付款日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="voucherNo" label="凭证号">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="remark" label="备注">
                <Input maxLength={128} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Row>
  );
}
